/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2019-10-02 12:07:54
 *
 *******************************************************/
// @flow

import React, { Component } from 'react';
import { Field } from 'redux-form';
import {
  FormLayout,
  Button,
  TextStyle,
  RangeSlider,
  SkeletonDisplayText,
  Link
} from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';

import { RenderTextField } from 'src/components/views/common/forms/render-textfield';
import { RenderChoice } from 'src/components/views/common/forms/render-choice';
import { normalizeNumberRangeSlider } from 'src/helpers/form-validation-helper';
import { URL } from 'src/constants/links';
import { buildOptionList } from 'src/helpers';
import { FUZZY_SEARCH_OPTIONS } from 'src/constants/search-settings';

type Props = {
  loading: boolean,
  minimumMustMatch: number,
  fuzzySearch: string,
  resetDefault: () => void,
  updateField: () => void
};

type ComposedProps = Props & WithI18nProps;

class Advanced extends Component<ComposedProps> {
  render() {
    const { i18n, loading, minimumMustMatch, resetDefault, updateField } = this.props;

    const fuzzySearchList = buildOptionList(FUZZY_SEARCH_OPTIONS, i18n);

    return (
      <div className="advanced">
        {/* Search Term */}
        <div className="heading searchTerm">
          {i18n.translate('AdminHTML.SearchSettings.Advanced.searchTerm')}
        </div>
        <FormLayout>
          <div className="btn-reset">
            <Button plain size="slim" onClick={resetDefault}>
              {i18n.translate('AdminHTML.Common.Action.reset')}
            </Button>
          </div>

          <p>
            {i18n.translate('AdminHTML.SearchSettings.Advanced.description', {
              t1: (
                <Link external url={URL['AdvancedSearchSettingsDoc']}>
                  {i18n.translate('AdminHTML.SearchSettings.Advanced.description_t1')}
                </Link>
              )
            })}
          </p>
          <div>
            <p>
              <TextStyle variation="strong">
                {i18n.translate('AdminHTML.SearchSettings.Advanced.twoWordsSearchTerm')}
              </TextStyle>
            </p>
            <p>{i18n.translate('AdminHTML.SearchSettings.Advanced.twoWordsSearchTerm_Note')}</p>
            {loading ? (
              <div style={{ marginTop: '1rem' }}>
                <SkeletonDisplayText size="small" />
              </div>
            ) : (
              <div className="range-slider-input">
                <RangeSlider
                  value={minimumMustMatch && minimumMustMatch.twoWords}
                  onChange={value => updateField('minimumMustMatch.twoWords', value)}
                />
                <Field
                  name="minimumMustMatch.twoWords"
                  component={RenderTextField}
                  type="number"
                  onBlur={(event, value) =>
                    this.onBlurTextField(event, value, 'minimumMustMatch.twoWords')
                  }
                  normalize={(value, prevValue) => normalizeNumberRangeSlider(value, prevValue)}
                />
              </div>
            )}
          </div>
          <div>
            <p>
              <TextStyle variation="strong">
                {i18n.translate('AdminHTML.SearchSettings.Advanced.moreWordsSearchTerm')}
              </TextStyle>
            </p>
            <p>{i18n.translate('AdminHTML.SearchSettings.Advanced.moreWordsSearchTerm_Note')}</p>
            {loading ? (
              <div style={{ marginTop: '1rem' }}>
                <SkeletonDisplayText size="small" />
              </div>
            ) : (
              <div className="range-slider-input">
                <RangeSlider
                  value={minimumMustMatch && minimumMustMatch.threeWordsAndAbove}
                  onChange={value => updateField('minimumMustMatch.threeWordsAndAbove', value)}
                />
                <Field
                  name="minimumMustMatch.threeWordsAndAbove"
                  component={RenderTextField}
                  type="number"
                  onBlur={(event, value) =>
                    this.onBlurTextField(event, value, 'minimumMustMatch.threeWordsAndAbove')
                  }
                  normalize={(value, prevValue) => normalizeNumberRangeSlider(value, prevValue)}
                />
              </div>
            )}
          </div>
        </FormLayout>

        {/* Fuzzy Search */}
        <div className="heading fuzzy">
          {i18n.translate('AdminHTML.SearchSettings.Advanced.fuzzySearch')}
        </div>
        <FormLayout>
          <p>
            {i18n.translate('AdminHTML.SearchSettings.Advanced.fuzzySearchDescription', {
              t1: (
                <Link external url={URL['AdvancedFuzzySearchDoc']}>
                  {i18n.translate('AdminHTML.SearchSettings.Advanced.fuzzySearchDescription_t1')}
                </Link>
              )
            })}
          </p>
          <div className="fuzzySearchSetting">
            {loading ? (
              <div style={{ marginTop: '1rem' }}>
                <SkeletonDisplayText size="small" />
              </div>
            ) : (
              <Field name="fuzzySearch" component={RenderChoice} choices={fuzzySearchList} />
            )}
          </div>
        </FormLayout>
      </div>
    );
  }

  // Handle blur input
  onBlurTextField = (event, value, name) => {
    const { updateField } = this.props;
    if (!value) {
      event.preventDefault();
      updateField(name, '0');
    }
  };
}
export default withI18n()(Advanced);
